/**
 * commentsYMap.js
 *
 * CRUD API for comments stored in Y.Map("comments").
 * Comments are fully client-owned — both written and read by the client.
 *
 * Structure inside the Y.Doc:
 *
 *   Y.Map("comments")
 *     └── [threadId]: Y.Map                ← threadId = "{elementName}:{elementId}"
 *           ├── _meta: Y.Map               ← thread-level metadata
 *           │     ├── createdAt             ← Date.now()
 *           │     ├── createdBy             ← userId
 *           │     ├── status                ← "open" | "resolved"
 *           │     ├── assignedTo            ← userId | null
 *           │     ├── resolvedAt            ← number | null
 *           │     └── resolvedBy            ← string | null
 *           └── messages: Y.Array           ← ordered append-only log of Y.Map entries
 *                 └── Y.Map                 ← individual message (messageId inside)
 *
 * threadId convention mirrors the existing RTDB path `/comments/{elementName}/{elementId}`:
 *   RTDB path   → /comments/report-fragment/abc123
 *   Y.Map key   → "report-fragment:abc123"
 *
 * Messages use hard delete (no soft delete) — there is no undo requirement for comments.
 * Y.Array gives guaranteed insertion order across all peers (no timestamp sorting needed).
 * Every write is wrapped in ydoc.transact() for atomicity.
 */

import * as Y from 'yjs';
import {generateUUID} from '../helpers/utils';

// ---------------------------------------------------------------------------
// Internal key constants
// ---------------------------------------------------------------------------

const META_KEY = '_meta';
const MESSAGES_KEY = 'messages';

// ---------------------------------------------------------------------------
// threadId helpers
// ---------------------------------------------------------------------------

/**
 * Build a threadId string from elementName + elementId.
 * Mirrors the RTDB path convention used by MessageInput.js.
 *
 * @param {string} elementName  e.g. "report-fragment"
 * @param {string} elementId    e.g. "abc123"
 * @returns {string}            e.g. "report-fragment:abc123"
 */
export function buildThreadId(elementName, elementId) {
    if (!elementName || !elementId) throw new Error('[commentsYMap] elementName and elementId are required');
    return `${elementName}:${elementId}`;
}

/**
 * Parse a threadId back into { elementName, elementId }.
 * Useful when iterating yComments keys and needing to filter by elementName.
 *
 * @param {string} threadId
 * @returns {{ elementName: string, elementId: string }}
 */
export function parseThreadId(threadId) {
    if (!threadId) return {elementName: '', elementId: ''};
    const colonIdx = threadId.indexOf(':');
    if (colonIdx === -1) return {elementName: threadId, elementId: ''};
    return {
        elementName: threadId.slice(0, colonIdx),
        elementId: threadId.slice(colonIdx + 1),
    };
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Get or create the thread Y.Map for a given threadId.
 * Also ensures _meta and messages sub-structures exist.
 * Must be called inside a ydoc.transact() block.
 *
 * @param {import('yjs').Map} yComments
 * @param {string}            threadId
 * @param {{ userId?: string }} opts  Options for initial _meta creation
 * @returns {import('yjs').Map}
 */
function getOrCreateThread(yComments, threadId, opts = {}) {
    if (!yComments.has(threadId)) {
        const thread = new Y.Map();

        // Create _meta with defaults
        const meta = new Y.Map();
        meta.set('createdAt', Date.now());
        meta.set('createdBy', opts.userId ?? null);
        meta.set('status', 'open');
        meta.set('assignedTo', null);
        meta.set('resolvedAt', null);
        meta.set('resolvedBy', null);
        thread.set(META_KEY, meta);

        // Create empty messages array
        thread.set(MESSAGES_KEY, new Y.Array());

        yComments.set(threadId, thread);
    }
    return yComments.get(threadId);
}

/**
 * Get the messages Y.Array from a thread, or null if thread doesn't exist.
 *
 * @param {import('yjs').Map} yComments
 * @param {string}            threadId
 * @returns {import('yjs').Array|null}
 */
function getMessagesArray(yComments, threadId) {
    const thread = yComments.get(threadId);
    if (!thread) return null;
    return thread.get(MESSAGES_KEY) ?? null;
}

// ---------------------------------------------------------------------------
// Deserialisation
// ---------------------------------------------------------------------------

/**
 * Deserialise a single message Y.Map into a plain JS object.
 * When contentType is "prosemirror" the stored JSON string is parsed back
 * into the original ProseMirror doc JSON object.
 *
 * @param {import('yjs').Map} msgMap
 * @returns {Object}
 */
function deserialiseMessage(msgMap) {
    const obj = {};
    msgMap.forEach((value, key) => {
        obj[key] = value;
    });
    // Parse stored prosemirror content back to an object
    if (obj.contentType === 'prosemirror' && typeof obj.content === 'string') {
        try {
            obj.content = JSON.parse(obj.content);
        } catch (e) {
            console.warn('[commentsYMap] Failed to parse prosemirror content JSON:', e);
        }
    }
    return obj;
}

// ---------------------------------------------------------------------------
// Public write API — messages
// ---------------------------------------------------------------------------

/**
 * Validate the content / contentType pair for addComment / editComment.
 *
 * Rules:
 *  - If contentType is "prosemirror":  content must be a non-empty object (ProseMirror JSON)
 *  - Otherwise (default "text"):       content must be a non-empty string
 *
 * @param {string|object} content
 * @param {string|undefined} contentType
 * @returns {{ content: string|object, contentType: string }}  normalised values
 * @throws if validation fails
 */
function validateContent(content, contentType) {
    const ct = contentType ?? 'text';
    if (ct === 'prosemirror') {
        if (!content || typeof content !== 'object') {
            throw new Error('[commentsYMap] content must be a non-empty object when contentType is "prosemirror"');
        }
        return {content, contentType: 'prosemirror'};
    }
    // default: "text"
    if (typeof content !== 'string' || !content.trim()) {
        throw new Error('[commentsYMap] content is required');
    }
    return {content: content.trim(), contentType: 'text'};
}

/**
 * Add a comment to a thread.
 *
 * A new UUID is generated for the message and stored as `messageId` inside the
 * Y.Map. The message is appended to the thread's messages Y.Array, which
 * preserves insertion order across all peers.
 *
 * If the thread doesn't exist yet, it is created with default _meta.
 *
 * @param {import('yjs').Doc} ydoc
 * @param {import('yjs').Map} yComments  Root Y.Map("comments")
 * @param {string}            threadId   "{elementName}:{elementId}"
 * @param {{
 *   content:           string|object,  string for "text", ProseMirror JSON object for "prosemirror"
 *   contentType?:      "text"|"prosemirror",  defaults to "text"
 *   publishedDatetime?: number,         defaults to Date.now()
 *   userId:            string,
 *   userDisplayName:   string,
 * }} commentData
 * @returns {string} The generated messageId
 */
export function addComment(ydoc, yComments, threadId, commentData) {
    if (!ydoc || !yComments) throw new Error('[commentsYMap] ydoc and yComments are required');
    if (!threadId) throw new Error('[commentsYMap] threadId is required');
    if (!commentData?.userId) throw new Error('[commentsYMap] userId is required');

    const {content, contentType} = validateContent(commentData.content, commentData.contentType);

    const messageId = generateUUID();

    ydoc.transact(() => {
        const thread = getOrCreateThread(yComments, threadId, {userId: commentData.userId});
        const messages = thread.get(MESSAGES_KEY);

        const msgMap = new Y.Map();
        msgMap.set('messageId', messageId);
        // Store objects as JSON strings so Yjs can sync them across peers
        msgMap.set('content', contentType === 'prosemirror' ? JSON.stringify(content) : content);
        msgMap.set('contentType', contentType);
        msgMap.set('publishedDatetime', commentData.publishedDatetime ?? Date.now());
        msgMap.set('userId', commentData.userId);
        msgMap.set('userDisplayName', commentData.userDisplayName ?? '');

        messages.push([msgMap]);
    }, null); // null origin → tracked by UndoManager

    return messageId;
}

/**
 * Hard-delete a specific message from a thread.
 * Scans the messages Y.Array to find the index of the message with the given
 * messageId, then deletes it. O(n) on thread length, which is acceptable for
 * comment threads (typically <100 messages).
 *
 * If the thread has no remaining messages after deletion, the entire thread
 * entry is removed from yComments to keep the map tidy.
 *
 * @param {import('yjs').Doc} ydoc
 * @param {import('yjs').Map} yComments
 * @param {string}            threadId
 * @param {string}            messageId
 */
export function deleteComment(ydoc, yComments, threadId, messageId) {
    if (!ydoc || !yComments) throw new Error('[commentsYMap] ydoc and yComments are required');
    if (!threadId) throw new Error('[commentsYMap] threadId is required');
    if (!messageId) throw new Error('[commentsYMap] messageId is required');

    ydoc.transact(() => {
        const messages = getMessagesArray(yComments, threadId);
        if (!messages) return;

        // Scan for the message index
        let targetIdx = -1;
        for (let i = 0; i < messages.length; i++) {
            const msgMap = messages.get(i);
            if (msgMap && msgMap.get('messageId') === messageId) {
                targetIdx = i;
                break;
            }
        }

        if (targetIdx === -1) return; // not found — no-op

        messages.delete(targetIdx, 1);

        // If the thread is now empty, remove the thread entry to keep the map tidy
        if (messages.length === 0) {
            yComments.delete(threadId);
        }
    }, null);
}

/**
 * Delete an entire comment thread from yComments.
 * Removes the thread entry (including all messages and _meta) from the Y.Map.
 * This is the correct cleanup path when the ProseMirror comment mark is deleted.
 *
 * @param {import('yjs').Doc} ydoc
 * @param {import('yjs').Map} yComments
 * @param {string}            threadId  "{elementName}:{elementId}"
 */
export function deleteThread(ydoc, yComments, threadId) {
    if (!ydoc || !yComments) throw new Error('[commentsYMap] ydoc and yComments are required');
    if (!threadId) throw new Error('[commentsYMap] threadId is required');
    ydoc.transact(() => {
        if (yComments.has(threadId)) {
            yComments.delete(threadId);
        }
    }, null);
}

/**
 * Edit an existing comment's content.
 * Scans the messages Y.Array to find the message with the given messageId,
 * then updates its `content` (and `contentType`) and sets `editedAt`
 * to the current timestamp. O(n) scan, acceptable for comment threads.
 *
 * Supports both plain-text ("text") and rich-text ("prosemirror") content.
 * Pass a non-empty string for "text" or a ProseMirror doc JSON object for "prosemirror".
 *
 * @param {import('yjs').Doc} ydoc
 * @param {import('yjs').Map} yComments  Root Y.Map("comments")
 * @param {string}            threadId   "{elementName}:{elementId}"
 * @param {string}            messageId  UUID of the message to edit
 * @param {string|object}     newContent The new content (string or PM JSON)
 * @param {string}           [contentType] "text" (default) | "prosemirror"
 */
export function editComment(ydoc, yComments, threadId, messageId, newContent, contentType) {
    if (!ydoc || !yComments) throw new Error('[commentsYMap] ydoc and yComments are required');
    if (!threadId) throw new Error('[commentsYMap] threadId is required');
    if (!messageId) throw new Error('[commentsYMap] messageId is required');

    const {content, contentType: ct} = validateContent(newContent, contentType);

    ydoc.transact(() => {
        const messages = getMessagesArray(yComments, threadId);
        if (!messages) return;

        for (let i = 0; i < messages.length; i++) {
            const msgMap = messages.get(i);
            if (msgMap && msgMap.get('messageId') === messageId) {
                msgMap.set('content', ct === 'prosemirror' ? JSON.stringify(content) : content);
                msgMap.set('contentType', ct);
                msgMap.set('editedAt', Date.now());
                break;
            }
        }
    }, null);
}

// ---------------------------------------------------------------------------
// Public write API — thread metadata
// ---------------------------------------------------------------------------

/**
 * Update thread metadata fields. Merges with existing _meta values.
 *
 * @param {import('yjs').Doc} ydoc
 * @param {import('yjs').Map} yComments
 * @param {string}            threadId
 * @param {Object}            metaUpdates  Key-value pairs to set on _meta
 */
export function updateThreadMeta(ydoc, yComments, threadId, metaUpdates) {
    if (!ydoc || !yComments) throw new Error('[commentsYMap] ydoc and yComments are required');
    if (!threadId) throw new Error('[commentsYMap] threadId is required');

    ydoc.transact(() => {
        const thread = yComments.get(threadId);
        if (!thread) return;
        const meta = thread.get(META_KEY);
        if (!meta) return;

        Object.entries(metaUpdates).forEach(([key, value]) => {
            meta.set(key, value);
        });
    }, null);
}

/**
 * Resolve a thread — sets status to "resolved" with timestamp and userId.
 *
 * @param {import('yjs').Doc} ydoc
 * @param {import('yjs').Map} yComments
 * @param {string}            threadId
 * @param {string}            userId  The user who resolved the thread
 */
export function resolveThread(ydoc, yComments, threadId, userId) {
    updateThreadMeta(ydoc, yComments, threadId, {
        status: 'resolved',
        resolvedAt: Date.now(),
        resolvedBy: userId,
    });
}

/**
 * Reopen a previously resolved thread.
 *
 * @param {import('yjs').Doc} ydoc
 * @param {import('yjs').Map} yComments
 * @param {string}            threadId
 */
export function reopenThread(ydoc, yComments, threadId) {
    updateThreadMeta(ydoc, yComments, threadId, {
        status: 'open',
        resolvedAt: null,
        resolvedBy: null,
    });
}

// ---------------------------------------------------------------------------
// Public read API
// ---------------------------------------------------------------------------

/**
 * Return all comments for a thread as an ordered array.
 * Y.Array preserves insertion order across peers, so no sorting is needed.
 * The returned objects include messageId on every entry.
 *
 * @param {import('yjs').Map} yComments
 * @param {string}            threadId
 * @returns {Object[]}  Array of plain comment objects
 */
export function getCommentsForThread(yComments, threadId) {
    if (!yComments || !threadId) return [];

    const messages = getMessagesArray(yComments, threadId);
    if (!messages) return [];

    const result = [];
    for (let i = 0; i < messages.length; i++) {
        const msgMap = messages.get(i);
        if (msgMap) result.push(deserialiseMessage(msgMap));
    }
    return result;
}

/**
 * Return the number of messages in a thread.
 *
 * @param {import('yjs').Map} yComments
 * @param {string}            threadId
 * @returns {number}
 */
export function getCommentCount(yComments, threadId) {
    if (!yComments || !threadId) return 0;
    const messages = getMessagesArray(yComments, threadId);
    if (!messages) return 0;
    return messages.length;
}

/**
 * Return the thread _meta as a plain object.
 * Returns sensible defaults when the thread doesn't exist yet.
 *
 * @param {import('yjs').Map} yComments
 * @param {string}            threadId
 * @returns {Object}
 */
export function getThreadMeta(yComments, threadId) {
    const defaults = {
        createdAt: null,
        createdBy: null,
        status: 'open',
        assignedTo: null,
        resolvedAt: null,
        resolvedBy: null,
    };

    if (!yComments || !threadId) return defaults;

    const thread = yComments.get(threadId);
    if (!thread) return defaults;
    const meta = thread.get(META_KEY);
    if (!meta) return defaults;

    const obj = {...defaults};
    meta.forEach((value, key) => {
        obj[key] = value;
    });
    return obj;
}

/**
 * Return the number of threads that have at least one message,
 * optionally filtered to only count threads whose elementId is in filterUids.
 *
 * @param {import('yjs').Map} yComments
 * @param {Set<string>|null}  filterUids   Set of elementId strings to filter by
 * @returns {number}
 */
export function getThreadsWithComments(yComments, filterUids) {
    if (!yComments) return 0;

    const useFilter = filterUids instanceof Set && filterUids.size > 0;
    let count = 0;

    yComments.forEach((thread, threadId) => {
        if (!thread) return;
        const messages = thread.get(MESSAGES_KEY);
        if (!messages || messages.length === 0) return;

        if (useFilter) {
            const {elementId} = parseThreadId(threadId);
            if (!filterUids.has(elementId)) return;
        }

        count++;
    });

    return count;
}

// ---------------------------------------------------------------------------
// Observation
// ---------------------------------------------------------------------------

/**
 * Subscribe to changes in a specific thread.
 * Fires whenever a message is added/deleted or _meta changes in that thread.
 *
 * @param {import('yjs').Map} yComments
 * @param {string}            threadId
 * @param {Function}          callback  (events: Y.YEvent[]) => void
 * @returns {Function} Unsubscribe function
 */
export function observeThread(yComments, threadId, callback) {
    if (!yComments || !threadId || typeof callback !== 'function') return () => {
    };

    const handler = (events) => {
        const relevant = events.some((event) =>
            (event.path.length === 0 && event.keysChanged && event.keysChanged.has(threadId))
            || (event.path.length > 0 && event.path[0] === threadId)
        );
        if (relevant) callback(events);
    };

    yComments.observeDeep(handler);
    return () => yComments.unobserveDeep(handler);
}

/**
 * Subscribe to any change anywhere inside Y.Map("comments").
 *
 * @param {import('yjs').Map} yComments
 * @param {Function}          callback  (events: Y.YEvent[]) => void
 * @returns {Function} Unsubscribe function
 */
export function observeAllComments(yComments, callback) {
    if (!yComments || typeof callback !== 'function') return () => {
    };
    yComments.observeDeep(callback);
    return () => yComments.unobserveDeep(callback);
}

